// Import directly from auth-store instead of barrel export to avoid loading all stores
import { useAuthStore } from '../stores/auth-store';

// Base API configuration from environment variables
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Supported response parsing strategies
export type BaseFetchResponseType = 'json' | 'blob' | 'text' | 'void';

interface ApiRequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  responseType?: BaseFetchResponseType;
  /**
   * When true, a 401 from this call does NOT trigger the global auto-logout
   * cleanup. Use for background checks (e.g. subscription status) that must
   * never nuke the session / clear the query cache on their own 401.
   */
  skipAuthLogout?: boolean;
}

// Get authentication token from Zustand store
const getAuthToken = (): string | null => {
  const { token } = useAuthStore.getState();
  return token;
};

// Get default headers with authentication token
const getDefaultHeaders = (body?: RequestInit['body']): HeadersInit => {
  const headers: HeadersInit = {
    'Accept': 'application/json',
  };

  // Avoid setting Content-Type when sending FormData
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  // Add Authorization header if token exists
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

export interface ApiClientConfig {
  /** Base URL for this client. Defaults to VITE_API_BASE_URL. */
  baseUrl?: string;
  /**
   * If true, skip the Laravel-style `{ success, code, data }` envelope check
   * and return the parsed JSON as-is. Use for services that don't wrap responses.
   */
  rawResponse?: boolean;
  /** When true, a 401 does NOT trigger the global auto-logout cleanup. */
  skipAuthLogout?: boolean;
}

// Base fetch wrapper with error handling
export const baseFetch = async <T>(
  endpoint: string,
  options: RequestInit = {},
  responseType: BaseFetchResponseType = 'json',
  config: ApiClientConfig = {}
): Promise<T> => {
  // Ensure proper URL construction with single slash
  const rawBase = config.baseUrl ?? API_BASE_URL;
  const baseUrl = rawBase?.endsWith('/') ? rawBase.slice(0, -1) : rawBase;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${baseUrl}${cleanEndpoint}`;
  
  // Merge headers - custom headers override default headers
  const headers = {
    ...getDefaultHeaders(options.body),
    ...(options.headers || {}),
  };
  
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Try to extract error message from response body
    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    let errorData: Record<string, unknown> | null = null;
    
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        // Clone response to read body without consuming it
        const clonedResponse = response.clone();
        errorData = await clonedResponse.json();
        errorMessage = (errorData?.message as string) || (errorData?.error as string) || errorMessage;
      }
    } catch {
      // Ignore JSON parsing errors and fall back to default message
    }
    
    // For 401, perform full logout cleanup (FCM, Firebase Installation, etc.)
    // This ensures security - prevents FCM tokens and cached data from leaking to next user
    if (response.status === 401) {
      // Auto-logout asynchronously (don't block the error throw): clears the
      // session and hard-redirects to login so the failing page unmounts and
      // its queries stop refetching. Skipped when this call opted out (e.g. a
      // background subscription check).
      if (!config.skipAuthLogout) {
        (async () => {
          const { performAutoLogout } = await import('./cleanup');
          await performAutoLogout();
        })();
      }

      const error = new Error(errorMessage);
      (error as unknown as Record<string, unknown>).status = 401;
      (error as unknown as Record<string, unknown>).code = 401;
      (error as unknown as Record<string, unknown>).data = errorData;
      (error as unknown as Record<string, unknown>).skipAuthLogout =
        config.skipAuthLogout === true;
      throw error;
    }

    // Create error with status and data
    const error = new Error(errorMessage);
    (error as unknown as Record<string, unknown>).status = response.status;
    (error as unknown as Record<string, unknown>).code = errorData?.code || response.status;
    (error as unknown as Record<string, unknown>).data = errorData;
    throw error;
  }

  if (responseType === 'void') {
    return undefined as T;
  }

  if (responseType === 'blob') {
    return (await response.blob()) as T;
  }

  if (responseType === 'text') {
    return (await response.text()) as T;
  }

  const data = await response.json();

  // Raw-response clients (e.g. easydo-api) return plain JSON, skip envelope checks
  if (config.rawResponse) {
    return data as T;
  }

  // Handle 401 in response data (some APIs return 401 in the response body)
  if (data.code === 401) {
    // Auto-logout asynchronously (don't block the error throw). Skipped when
    // this call opted out (e.g. a background subscription check).
    if (!config.skipAuthLogout) {
      (async () => {
        const { performAutoLogout } = await import('./cleanup');
        await performAutoLogout();
      })();
    }

    const error = new Error(data.message || 'Unauthenticated');
    (error as unknown as Record<string, unknown>).skipAuthLogout =
      config.skipAuthLogout === true;
    (error as unknown as Record<string, unknown>).status = 401;
    (error as unknown as Record<string, unknown>).code = 401;
    throw error;
  }
  
  // Check for success - handle both success field and code field
  // Some APIs use success field, others use code field (200 = success)
  // If success field exists, use it; otherwise check if code is 200
  const hasSuccessField = 'success' in data;
  const isSuccess = hasSuccessField 
    ? data.success === true 
    : data.code === 200; // If no success field, treat code 200 as success
  
  // Even if success is true, check if data is null and message indicates error
  // This handles cases where API returns {success: true, data: null, message: "User not found"}
  if (isSuccess && data.data === null && data.message) {
    const errorMessage = data.message.toLowerCase();
    if (errorMessage.includes('not found') || 
        errorMessage.includes('error') || 
        errorMessage.includes('invalid') ||
        errorMessage.includes('failed')) {
      throw new Error(data.message);
    }
  }
  
  if (!isSuccess) {
    // Carry the body's own code (and payload) on the error. Without it every
    // body-level failure arrived as a bare Error and callers could not tell one
    // from another -- notably code 202, which this API uses for "there is
    // nothing here", a real answer rather than a fault. `getCompany` reads it to
    // turn "you belong to no company" back into an empty list; anything that
    // only checks `.message` is unaffected.
    const error = new Error(data.message || 'API request failed');
    (error as unknown as Record<string, unknown>).code = data.code;
    (error as unknown as Record<string, unknown>).data = data;
    throw error;
  }

  return data;
};

// POST request with JSON body
export const apiPost = async <T>(
  endpoint: string,
  data: object,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { responseType, skipAuthLogout, ...rest } = options;
  return baseFetch<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
    ...rest,
  }, responseType, { skipAuthLogout });
};

// POST request with FormData body
export const apiPostForm = async <T>(
  endpoint: string,
  formData: FormData,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { responseType, skipAuthLogout, ...rest } = options;
  return baseFetch<T>(endpoint, {
    method: 'POST',
    body: formData,
    ...rest,
  }, responseType, { skipAuthLogout });
};

// GET request
export const apiGet = async <T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { responseType, skipAuthLogout, ...rest } = options;
  return baseFetch<T>(endpoint, {
    method: 'GET',
    ...rest,
  }, responseType, { skipAuthLogout });
};

// PUT request
export const apiPut = async <T>(
  endpoint: string,
  data: object,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { responseType, skipAuthLogout, ...rest } = options;
  return baseFetch<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...rest,
  }, responseType, { skipAuthLogout });
};

// PATCH request
export const apiPatch = async <T>(
  endpoint: string,
  data: object,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { responseType, skipAuthLogout, ...rest } = options;
  return baseFetch<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
    ...rest,
  }, responseType, { skipAuthLogout });
};

// DELETE request
export const apiDelete = async <T>(
  endpoint: string,
  // Some endpoints take their arguments in the body of a DELETE rather than the
  // query string -- `branches/wifi` reads branch_id and ids from it -- so this
  // has to be able to send one. Omitted, no body is sent and existing callers
  // are unaffected.
  data?: object,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { responseType, skipAuthLogout, ...rest } = options;
  return baseFetch<T>(endpoint, {
    method: 'DELETE',
    ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
    ...rest,
  }, responseType, { skipAuthLogout });
};

/**
 * Create an API client bound to a different base URL (and optional response
 * handling). Returns the same set of verbs as the top-level exports:
 *
 *     const { apiGet, apiPut } = createApiClient({
 *       baseUrl: 'http://localhost:8787',
 *       rawResponse: true,
 *     });
 */
export const createApiClient = (config: ApiClientConfig) => {
  // A per-call `skipAuthLogout` wins over the client-wide one; without it the
  // client's own setting stands.
  const run = <T>(
    endpoint: string,
    init: RequestInit,
    responseType?: BaseFetchResponseType,
    skipAuthLogout?: boolean,
  ) =>
    baseFetch<T>(endpoint, init, responseType, {
      ...config,
      ...(skipAuthLogout !== undefined ? { skipAuthLogout } : {}),
    });

  return {
    apiGet: <T>(endpoint: string, options: ApiRequestOptions = {}) => {
      const { responseType, skipAuthLogout, ...rest } = options;
      return run<T>(endpoint, { method: 'GET', ...rest }, responseType, skipAuthLogout);
    },
    apiPost: <T>(endpoint: string, data: object, options: ApiRequestOptions = {}) => {
      const { responseType, skipAuthLogout, ...rest } = options;
      return run<T>(
        endpoint,
        { method: 'POST', body: JSON.stringify(data), ...rest },
        responseType,
        skipAuthLogout,
      );
    },
    apiPostForm: <T>(endpoint: string, formData: FormData, options: ApiRequestOptions = {}) => {
      const { responseType, skipAuthLogout, ...rest } = options;
      return run<T>(
        endpoint,
        { method: 'POST', body: formData, ...rest },
        responseType,
        skipAuthLogout,
      );
    },
    apiPut: <T>(endpoint: string, data: object, options: ApiRequestOptions = {}) => {
      const { responseType, skipAuthLogout, ...rest } = options;
      return run<T>(
        endpoint,
        { method: 'PUT', body: JSON.stringify(data), ...rest },
        responseType,
        skipAuthLogout,
      );
    },
    apiPatch: <T>(endpoint: string, data: object, options: ApiRequestOptions = {}) => {
      const { responseType, skipAuthLogout, ...rest } = options;
      return run<T>(
        endpoint,
        { method: 'PATCH', body: JSON.stringify(data), ...rest },
        responseType,
        skipAuthLogout,
      );
    },
    apiDelete: <T>(endpoint: string, options: ApiRequestOptions = {}) => {
      const { responseType, skipAuthLogout, ...rest } = options;
      return run<T>(endpoint, { method: 'DELETE', ...rest }, responseType, skipAuthLogout);
    },
  };
};
